<template>
  <PrimevueDialog
    :visible="visible"
    :modal="true"
    :closable="!isSubmitting"
    :style="{ width: '960px', maxWidth: '96vw' }"
    @update:visible="updateVisible"
  >
    <template #header>
      <h5 class="mb-0">
        <i class="fa fa-code-merge me-2 text-primary"></i>
        Fusionner des produits
      </h5>
    </template>

    <div class="merge-steps mb-3">
      <span class="step-pill" :class="{ active: step === 'selection' }">1. Sélection</span>
      <span class="step-pill" :class="{ active: step === 'details' }">2. Détails</span>
      <span class="step-pill" :class="{ active: step === 'recap' }">3. Récap</span>
    </div>

    <div v-if="errorMessage" class="alert alert-danger py-2">
      {{ errorMessage }}
    </div>

    <section v-if="step === 'selection'" class="merge-step">
      <p class="text-muted small mb-3">
        Recherchez les produits sources à fusionner. Le stock et le cumul seront déplacés vers le nouveau produit.
      </p>

      <ProductSearchPicker
        placeholder="Rechercher nom, SKU ou code-barres..."
        :show-stock="true"
        :show-price="false"
        :show-quantity="false"
        :require-search-term="true"
        :disabled="isSubmitting"
        @add="addSourceProduct"
      />

      <div class="selected-products mt-3">
        <div class="d-flex align-items-center justify-content-between gap-2 mb-2">
          <h6 class="mb-0">Produits sélectionnés</h6>
          <span class="badge bg-light text-dark border">{{ selectedProducts.length }} produit(s)</span>
        </div>

        <div v-if="!selectedProducts.length" class="empty-selection">
          Aucun produit sélectionné.
        </div>

        <div v-else class="table-responsive">
          <table class="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th>Produit</th>
                <th>SKU</th>
                <th class="text-end">Stock</th>
                <th class="text-end">Cumul</th>
                <th class="text-end">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="product in selectedProducts" :key="product.id">
                <td class="fw-semibold">{{ product.name }}</td>
                <td>{{ product.sku || '-' }}</td>
                <td class="text-end">{{ stockQuantity(product) }}</td>
                <td class="text-end">{{ cumulativeStockQuantity(product) }}</td>
                <td class="text-end">
                  <button
                    type="button"
                    class="btn btn-outline-danger btn-sm btn-action-icon"
                    title="Retirer"
                    :disabled="isSubmitting"
                    @click="removeSourceProduct(product.id)"
                  >
                    <i class="fa fa-times"></i>
                  </button>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <th colspan="2">Total déplacé</th>
                <th class="text-end">{{ totalStockQuantity }}</th>
                <th class="text-end">{{ totalCumulativeStockQuantity }}</th>
                <th></th>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </section>

    <section v-else-if="step === 'details'" class="merge-step">
      <div class="row g-3">
        <div class="col-md-6">
          <label class="form-label fw-semibold">Nom du nouveau produit</label>
          <PrimevueInputText
            :modelValue="form.name"
            class="w-100"
            placeholder="Ex : Pain mixte"
            @update:modelValue="updateName"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label fw-semibold">SKU du nouveau produit</label>
          <PrimevueInputText
            :modelValue="form.sku"
            class="w-100"
            placeholder="Ex : PAIN-MIXTE"
            @update:modelValue="updateSku"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label fw-semibold">Rayon</label>
          <PrimevueDropdown
            v-model="form.rayon_id"
            :options="rayons"
            optionLabel="name"
            optionValue="id"
            class="w-100"
            filter
            placeholder="Sélectionner un rayon"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label fw-semibold">Catégorie</label>
          <PrimevueDropdown
            v-model="form.category_id"
            :options="normalizedCategories"
            optionLabel="label"
            optionValue="id"
            class="w-100"
            filter
            placeholder="Sélectionner une catégorie"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label fw-semibold">Prix d’achat</label>
          <small class="d-block text-muted mb-1">Optionnel</small>
          <PrimevueInputNumber
            v-model="form.purchase_price"
            class="w-100"
            mode="decimal"
            :min="0"
            :maxFractionDigits="2"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label fw-semibold">Prix de vente</label>
          <PrimevueInputNumber
            v-model="form.sale_price"
            class="w-100"
            mode="decimal"
            :min="0"
            :maxFractionDigits="2"
          />
        </div>
      </div>
    </section>

    <section v-else class="merge-step">
      <div class="recap-grid">
        <div class="recap-panel">
          <h6>Nouveau produit</h6>
          <dl class="mb-0">
            <div>
              <dt>Nom</dt>
              <dd>{{ form.name }}</dd>
            </div>
            <div>
              <dt>SKU</dt>
              <dd>{{ form.sku }}</dd>
            </div>
            <div>
              <dt>Rayon</dt>
              <dd>{{ selectedRayonLabel }}</dd>
            </div>
            <div>
              <dt>Catégorie</dt>
              <dd>{{ selectedCategoryLabel }}</dd>
            </div>
            <div>
              <dt>Prix d’achat</dt>
              <dd>{{ $format.currency(form.purchase_price || 0) }}</dd>
            </div>
            <div>
              <dt>Prix de vente</dt>
              <dd>{{ $format.currency(form.sale_price || 0) }}</dd>
            </div>
          </dl>
        </div>

        <div class="recap-panel">
          <h6>Stock déplacé</h6>
          <div class="recap-total">
            <span>Stock actuel</span>
            <strong>{{ totalStockQuantity }}</strong>
          </div>
          <div class="recap-total">
            <span>Cumul stock</span>
            <strong>{{ totalCumulativeStockQuantity }}</strong>
          </div>
          <div class="form-check mt-3">
            <PrimevueCheckbox
              v-model="deactivateSources"
              binary
              inputId="mergeDeactivateSources"
            />
            <label for="mergeDeactivateSources" class="form-check-label ms-2">
              Désactiver les produits source après fusion
            </label>
          </div>
        </div>
      </div>

      <div class="table-responsive mt-3">
        <table class="table table-sm align-middle mb-0">
          <thead>
            <tr>
              <th>Produit source</th>
              <th>SKU</th>
              <th class="text-end">Stock retiré</th>
              <th class="text-end">Cumul retiré</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="product in selectedProducts" :key="product.id">
              <td class="fw-semibold">{{ product.name }}</td>
              <td>{{ product.sku || '-' }}</td>
              <td class="text-end">{{ stockQuantity(product) }}</td>
              <td class="text-end">{{ cumulativeStockQuantity(product) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <template #footer>
      <div class="d-flex justify-content-between align-items-center gap-2 w-100">
        <button type="button" class="btn btn-outline-secondary" :disabled="isSubmitting" @click="close">
        <i class="fa fa-xmark me-1" aria-hidden="true"></i>
        Annuler
        </button>

        <div class="d-flex justify-content-end gap-2">
          <button
            v-if="step !== 'selection'"
            type="button"
            class="btn btn-outline-secondary btn-icon-label"
            :disabled="isSubmitting"
            @click="goPrevious"
          >
            <i class="fa fa-arrow-left"></i>
            <span class="btn-label ms-1">Précédent</span>
          </button>

          <button
            v-if="step !== 'recap'"
            type="button"
            class="btn btn-primary btn-icon-label"
            :disabled="isSubmitting"
            @click="goNext"
          >
            <i class="fa fa-arrow-right"></i>
            <span class="btn-label ms-1">Suivant</span>
          </button>

          <AppLoadingButton
            v-else
            type="button"
            class="btn btn-primary btn-icon-label"
            :loading="isSubmitting"
            loading-label="Fusion..."
            @click="submitMerge"
          >
            <i class="fa fa-code-merge"></i>
            <span class="btn-label ms-1">Fusionner</span>
          </AppLoadingButton>
        </div>
      </div>
    </template>
  </PrimevueDialog>
</template>

<script>
import ProductSearchPicker from '@/components/ProductSearchPicker.vue';
import { extractApiErrorMessage } from '@/support/apiErrors';

const STEPS = ['selection', 'details', 'recap'];

export default {
  name: 'ProductMergeDialog',
  components: {
    ProductSearchPicker,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    categories: {
      type: Array,
      default: () => [],
    },
    rayons: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update:visible', 'merged'],
  data() {
    return this.initialState();
  },
  computed: {
    totalStockQuantity() {
      return this.selectedProducts.reduce((total, product) => total + this.stockQuantity(product), 0);
    },
    totalCumulativeStockQuantity() {
      return this.selectedProducts.reduce((total, product) => total + this.cumulativeStockQuantity(product), 0);
    },
    normalizedCategories() {
      return this.categories.map((category) => ({
        id: category.id,
        label: category.label || (category.code ? `${category.code} - ${category.name}` : category.name),
      }));
    },
    selectedRayonLabel() {
      return this.rayons.find((rayon) => Number(rayon.id) === Number(this.form.rayon_id))?.name || '-';
    },
    selectedCategoryLabel() {
      return this.normalizedCategories.find((category) => Number(category.id) === Number(this.form.category_id))?.label || '-';
    },
  },
  watch: {
    visible(value) {
      if (value) {
        Object.assign(this.$data, this.initialState());
      }
    },
  },
  methods: {
    initialState() {
      return {
        step: 'selection',
        selectedProducts: [],
        form: {
          name: '',
          sku: '',
          rayon_id: null,
          category_id: null,
          purchase_price: null,
          sale_price: null,
        },
        skuManuallyEdited: false,
        deactivateSources: true,
        errorMessage: '',
        isSubmitting: false,
      };
    },
    updateVisible(value) {
      if (!value && this.isSubmitting) return;
      this.$emit('update:visible', value);
    },
    close() {
      if (this.isSubmitting) return;
      this.$emit('update:visible', false);
    },
    addSourceProduct(payload) {
      const product = payload?.product || payload;
      if (!product?.id) return;

      if (this.selectedProducts.some((selected) => Number(selected.id) === Number(product.id))) {
        this.errorMessage = 'Ce produit est déjà dans la sélection.';
        return;
      }

      this.errorMessage = '';
      this.selectedProducts.push({ ...product });
    },
    removeSourceProduct(productId) {
      this.selectedProducts = this.selectedProducts.filter((product) => Number(product.id) !== Number(productId));
    },
    goNext() {
      this.errorMessage = '';

      if (this.step === 'selection' && this.selectedProducts.length < 2) {
        this.errorMessage = 'Sélectionnez au moins deux produits à fusionner.';
        return;
      }

      if (this.step === 'details' && !this.detailsAreValid()) {
        this.errorMessage = 'Renseignez le nom, le SKU, le rayon, la catégorie et le prix de vente.';
        return;
      }

      const currentIndex = STEPS.indexOf(this.step);
      this.step = STEPS[Math.min(currentIndex + 1, STEPS.length - 1)];
    },
    goPrevious() {
      this.errorMessage = '';
      const currentIndex = STEPS.indexOf(this.step);
      this.step = STEPS[Math.max(currentIndex - 1, 0)];
    },
    detailsAreValid() {
      return String(this.form.name || '').trim() !== ''
        && String(this.form.sku || '').trim() !== ''
        && Boolean(this.form.rayon_id)
        && Boolean(this.form.category_id)
        && this.numericOrNull(this.form.sale_price) !== null;
    },
    numericOrNull(value) {
      if (value === null || value === undefined || value === '') return null;

      const number = Number(value);
      return Number.isFinite(number) && number >= 0 ? number : null;
    },
    payload() {
      return {
        source_product_ids: this.selectedProducts.map((product) => product.id),
        name: String(this.form.name || '').trim(),
        sku: String(this.form.sku || '').trim(),
        rayon_id: this.form.rayon_id || null,
        category_id: this.form.category_id || null,
        purchase_price: this.numericOrNull(this.form.purchase_price),
        sale_price: this.numericOrNull(this.form.sale_price) ?? 0,
        deactivate_sources: Boolean(this.deactivateSources),
      };
    },
    async submitMerge() {
      if (this.isSubmitting) return;

      if (!this.detailsAreValid() || this.selectedProducts.length < 2) {
        this.errorMessage = 'Vérifiez la sélection et les informations du nouveau produit.';
        return;
      }

      this.isSubmitting = true;
      this.errorMessage = '';

      try {
        const response = await this.$api.post('/products/merge', this.payload());
        this.$emit('merged', response.data?.data || response.data);
        this.$emit('update:visible', false);
      } catch (error) {
        this.errorMessage = extractApiErrorMessage(error, 'Impossible de fusionner les produits.');
      } finally {
        this.isSubmitting = false;
      }
    },
    stockQuantity(product) {
      return Number(product?.stock_quantity || 0);
    },
    cumulativeStockQuantity(product) {
      return Number(product?.cumulative_stock_quantity || 0);
    },
    updateName(value) {
      const nextName = String(value || '');
      this.form.name = nextName;

      if (!this.skuManuallyEdited || String(this.form.sku || '').trim() === '') {
        this.form.sku = this.skuFromName(nextName);
      }
    },
    updateSku(value) {
      const nextSku = String(value || '');
      this.form.sku = nextSku;
      this.skuManuallyEdited = nextSku.trim() !== '';
    },
    skuFromName(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 255);
    },
  },
};
</script>

<style scoped>
.merge-steps {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.step-pill {
  background: #eef2f7;
  border: 1px solid #d9e1ec;
  border-radius: 999px;
  color: #64748b;
  font-size: 0.82rem;
  font-weight: 700;
  padding: 0.35rem 0.75rem;
}

.step-pill.active {
  background: #0d6efd;
  border-color: #0d6efd;
  color: #fff;
}

.merge-step {
  min-height: 20rem;
}

.empty-selection {
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  color: #64748b;
  padding: 1rem;
  text-align: center;
}

.recap-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.recap-panel {
  border: 1px solid #dfe6f1;
  border-radius: 8px;
  padding: 1rem;
}

.recap-panel h6 {
  font-weight: 800;
  margin-bottom: 0.8rem;
}

.recap-panel dl > div {
  display: flex;
  gap: 1rem;
  justify-content: space-between;
}

.recap-panel dt {
  color: #64748b;
  font-size: 0.84rem;
  font-weight: 700;
}

.recap-panel dd {
  font-weight: 700;
  margin-bottom: 0.45rem;
  text-align: right;
}

.recap-total {
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 0.35rem 0;
}

@media (max-width: 768px) {
  .recap-grid {
    grid-template-columns: 1fr;
  }
}
</style>
